import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Firestore path: audit_log/{logId}
 *
 * Append-only trail for security-sensitive actions: cross-tenant admin
 * operations, role changes, document deletions, tenant-isolation refusals.
 *
 * Everything sensitive logs here. The BigQuery ai_usage_events table captures
 * LLM-call telemetry; audit_log captures user/admin actions that change state
 * or affect access.
 */
export const AuditAction = z.enum([
    'user.invited',
    'user.role_changed',
    'user.deactivated',
    'user.reactivated',
    'document.uploaded',
    'document.indexed',
    'document.deactivated',
    'document.deleted',
    'category.created',
    'category.merged',
    'category.deleted',
    'provider.created',
    'provider.updated',
    'provider.deactivated',
    'provider.deleted',
    'change_request.approved',
    'change_request.rejected',
    'tenant.created',
    'tenant.suspended',
    'ring2_source.added',
    'ring2_source.removed',
    'access.refused',
    'login.success',
    'login.failure',
    'kennisagent.query',
    'kennisagent.error',
    'kennisagent.feedback',
    'kennisagent.source_flagged',
    'kennisagent.export_docx',
    'kennisagent.export_pptx',
    // Canvas (issue #3) — persistent multi-turn artifact authoring.
    // .canvas_create on the first canvas turn; .canvas_update on subsequent
    // agent rewrites (slice 5); .canvas_restore when the user rolls back to
    // a prior version; .canvas_export on docx/pptx download.
    'kennisagent.canvas_create',
    'kennisagent.canvas_update',
    'kennisagent.canvas_restore',
    'kennisagent.canvas_export',
    'ingest.lock_cleared_stale_acquiring',
    'ingest.lock_cleared_stale_running',
    'ingest.janitor_error',
    'ingest.stuck_code_sources_reset',
    // Bulk upload (2026-09): batch header created; janitor housekeeping for
    // the server-side documents queue.
    'ingest.batch_created',
    'ingest.uploads_expired',
    'ingest.worker_lost',
    'ingest.upload_started',
    'ingest.upload_finalized',
    'ingest.reingest_started',
    'ingest.delete_executed',
    'ingest.job_failed',
    'ingest.kept_as_download_only',
    'document.metadata_edited',
    // Document-version families — admin review decisions.
    'document.version_family_confirmed',
    'document.version_member_split',
    'document.version_current_set',
    'document.version_family_standalone',
    'document.downloaded',
    // Secret-redaction migration — emitted per rewritten chunk by
    // backend/scripts/migrate-sanitise-chunks.mjs.
    'document.chunk_sanitised',
    'provider.enriched_from_website',
    // Phase 1.5 — Confluence Cloud ingest sources.
    'confluence.source_created',
    'confluence.source_deleted',
    'confluence.source_synced',
    'confluence.source_sync_queued',
    'confluence.source_backfilled',
    'confluence.source_metadata_refreshed',
    'confluence.page_resynced',
    // Jira Cloud project ingest sources.
    'jira.source_created',
    'jira.source_updated',
    'jira.source_deleted',
    'jira.source_sync_queued',
    // Per-page block/unblock for re-ingest control.
    'knowledgebase.document_unblocked',
    // Document-type taxonomy admin — bulk clear of stale tagger output.
    'document_type.retag_stale_cleared',
    // Code Sources — Bitbucket / Azure DevOps / GitHub via the unified gateway.
    'code_source.created',
    'code_source.updated',
    'code_source.deleted',
    'code_source.synced',
    'code_source.sync_queued',
    // ISO 27001 information classification.
    'kennisagent.confidential_retrieval',
    'document.classification_changed',
    'source.classification_defaults_changed',
    // Training-video ingest (Speech-to-Text v2 + Gemini visual summaries).
    // Mirrors the document.* lifecycle but on `tenants/{tenantId}/videos/{videoId}`.
    'video.uploaded',
    'video.ingest_started',
    'video.transcribed',
    'video.indexed',
    'video.failed',
    'video.deleted',
    'video.metadata_updated',
    'video_series.created',
    'video_series.updated',
    'video_series.deleted',
    // Data Extractor (Tier 1.5) — Slice 1 lifecycle hooks.
    'extractor.run_started',
    'extractor.run_completed',
    'extractor.run_failed',
    'extractor.run_refined',
    'extractor.run_downloaded',
    'extractor.combined_downloaded',
    'extractor.run_deleted',
    'extractor.template_created',
    'extractor.template_updated',
    // Extractor v2 — OneView destination-format registry.
    'extractor.destination_format_created',
    'extractor.destination_format_updated',
    'extractor.destination_format_deleted',
    // External integration layer — API-key auth surface (AG-UI + MCP).
    'api_key.created',
    'api_key.rotated',
    'api_key.revoked',
    'api_key.updated',
    'external_query',
    'external_query.auth_failed',
    'external_query.rate_limited',
    // Branded-document export templates (kb_admin / legal manage these).
    'doc_template.upload_version',
    'doc_template.set_active',
    'doc_template.archive',
    'doc_template.restore',
]);
export const AuditLogEntry = z.object({
    id: DocId,
    tenantId: TenantId,
    actorUserId: DocId.optional(),
    actorRole: z.string().max(50).optional(),
    action: AuditAction,
    /**
     * Target resource identifier in path form (e.g. `users/abc`, `documents/xyz`).
     */
    targetPath: z.string().max(500).optional(),
    /**
     * Sanitised context. MUST NOT contain patient data or raw clinical queries.
     * Query text may be reduced to a SHA-256 hash for de-duplication.
     */
    context: z.record(z.unknown()).optional(),
    at: IsoTimestamp,
});
//# sourceMappingURL=audit-log.js.map